import dayjs, { type Dayjs } from 'dayjs';

/**
 * THE ENGAGEMENT'S PERIOD DATES ARE ZURICH CALENDAR DAYS, and this pair is what keeps
 * the client honest about that.
 *
 * The `Audit` period columns (`financialYearStart` … `subsequentPeriodEnd`) are
 * timestamps, and the SERVER decides what day one means: it renders the stored instant
 * with `Intl` in `Europe/Zurich` and, when the instant is not Zurich midnight, anchors
 * the run to the day it had to interpret and flags the version `ambiguous`
 * (`analysisCoverage.js`). Sending the picker's LOCAL midnight — `toISOString()` on a
 * date the picker built in the browser's own zone — makes two different defects:
 *
 *   · WEST of Zurich the instant lands on the right Zurich day but never at midnight,
 *     so every run is flagged `ambiguous` and the wizard shows interpreted dates as if
 *     they were exact.
 *   · EAST of Zurich (Kyiv at UTC+2/+3, and further) local midnight renders in Zurich
 *     as the PREVIOUS calendar day, so the period starts a day early and entries on the
 *     real first day fall outside it. That one is silent and wrong.
 *
 * So a write sends the instant that IS Zurich midnight for the day the auditor clicked,
 * and a read turns a stored instant back into the Zurich day it means. The two must
 * move together: a Zurich-midnight instant read back in a west-of-Zurich browser renders
 * as the previous evening, and the picker would show the wrong day.
 *
 * Every writer of those columns goes through here — audit settings, the archive dialog,
 * and the import wizard's period picker — so the same day reaches the server whichever
 * screen saved it.
 */

/** Zurich's UTC offset in ms at one instant, read the same way the server reads it. */
const zurichOffsetAt = (utcMs: number): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Zurich',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(utcMs));
  const at = (type: string): number => Number(parts.find((part) => part.type === type)?.value);
  /* `hour` comes back as 24 at midnight under `hour12: false` in some ICU versions —
     normalised, because a 24 here would push the offset a whole day out. */
  const hour = at('hour') % 24;
  return Date.UTC(at('year'), at('month') - 1, at('day'), hour, at('minute'), at('second')) - utcMs;
};

/**
 * The instant that is 00:00 in Zurich on the given calendar day.
 *
 * TWO PASSES, because the offset depends on the instant we are solving for: the first
 * guess uses the offset at UTC midnight, and a day whose Zurich offset differs there
 * (the DST weekends) is corrected by the second.
 *
 * ⚠ MIDNIGHT ALWAYS EXISTS in Zurich — the transitions are at 02:00 and 03:00 — so
 * there is no "this local time does not occur" case to resolve. A zone with a midnight
 * transition would need a rule here.
 */
export const zurichMidnightIso = (year: number, month: number, day: number): string => {
  const wall = Date.UTC(year, month - 1, day);
  const first = wall - zurichOffsetAt(wall);
  const second = wall - zurichOffsetAt(first);
  return new Date(second).toISOString();
};

/**
 * A stored instant → the calendar day it means IN ZURICH, as the `@shared/ui` calendar
 * family speaks days: `'YYYY-MM-DD'`, or `null` for nothing stored or an unparseable value.
 *
 * Read straight off the Zurich parts, with no local `Date` in between, so the browser's
 * own zone has nothing to move.
 */
export const toZurichIsoDay = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Zurich',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const at = (type: string) => parts.find((part) => part.type === type)?.value;
  return `${at('year')}-${at('month')}-${at('day')}`;
};

/**
 * A stored instant → the calendar day it means IN ZURICH, as a local-midnight `Date`.
 *
 * Local midnight because that is what the pickers and day-grained comparisons expect;
 * the DAY is Zurich's, which is the part that has to be right.
 */
export const toZurichDay = (iso: string | null | undefined): Date | null => {
  const day = toZurichIsoDay(iso);
  if (!day) return null;
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
};

/**
 * A `'YYYY-MM-DD'` day → that day's Zurich-midnight instant, or `null` for no day or one
 * that does not exist (`2025-02-30` is refused, never rolled into March).
 *
 * The day is read and checked in UTC arithmetic rather than through the calendar's own
 * `isoToDate`, which builds a local `Date`: like the read above, nothing here consults the
 * browser's zone.
 */
export const zurichMidnightIsoOfDay = (day: string | null | undefined): string | null => {
  const match = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  if (!match) return null;
  const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(Date.UTC(year, month - 1, date));
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== date) return null;
  return zurichMidnightIso(year, month, date);
};

/** `toZurichDay` as a `Dayjs`: a stored instant → its Zurich day, for formatting. */
export const zurichDayjs = (iso: string | null | undefined): Dayjs | null => {
  const day = toZurichDay(iso);
  return day ? dayjs(day) : null;
};
